/**
 * 발사체 유도(homing) 조향 계산 (순수 — Phaser 비의존).
 *
 * Projectile.applyHoming의 결정 로직(목표각 계산·최단경로 회전·속도 벡터 재구성)을 추출한 것.
 * Phaser.Math.Angle.RotateTo를 순수 재구현(rotateTowards)해 vitest 환경에서 Phaser canvas 의존 없이
 * 테스트한다 (bounceBounds.ts와 동일한 "순수 계산 분리" 패턴). Projectile이 매 프레임 이 함수를 호출하므로
 * 단위테스트가 실제 런타임 조향 경로를 그대로 검증한다(회귀 방지).
 */

const PI2 = Math.PI * 2;

/** 2D 벡터 (위치/속도). NearestBrickLocator 반환값·Arcade body.velocity와 같은 모양. */
export interface Vec2 {
  x: number;
  y: number;
}

/** 유도 기본 선회율(rad/frame) — 기존 applyHoming 인라인 리터럴 0.06의 SSoT. */
export const DEFAULT_HOMING_TURN_RATE = 0.06;

/**
 * current 각(rad)에서 target 각(rad)으로 최단경로로 turnRate(rad)만큼 회전한 각을 반환.
 * Phaser.Math.Angle.RotateTo의 순수 포팅:
 *  - current와 target이 같으면 current 그대로 반환(회전 불필요)
 *  - 차이가 turnRate 이내(또는 PI2-turnRate 이상 = 반대로 거의 한 바퀴)면 target으로 스냅
 *  - 차이가 PI보다 크면 target을 ±PI2 보정해 더 짧은 회전 방향을 고른다
 *  - 그 방향으로 turnRate만큼만 이동(보정 후 target이 current와 같으면 current 폴백)
 * 반환각은 wrap하지 않는다 — 호출부가 cos/sin으로 쓰므로 무관.
 */
export function rotateTowards(current: number, target: number, turnRate: number): number {
  if (current === target) return current;
  const diff = Math.abs(target - current);
  if (diff <= turnRate || diff >= PI2 - turnRate) return target;
  let t = target;
  if (diff > Math.PI) {
    if (target < current) t = target + PI2;
    else t = target - PI2;
  }
  if (t > current) return current + turnRate;
  if (t < current) return current - turnRate;
  return current;
}

/**
 * 유도 조향 — (x,y)에서 속도(vx,vy)로 나아가는 발사체가 target을 향해 turnRate만큼 선회한 새 속도.
 * 속도 크기(speed)는 보존하고 방향만 회전한다. 조향하지 않아야 하면 null을 반환:
 *  - target이 null (추적 대상 없음)
 *  - 속도가 0 (방향이 정의되지 않음)
 */
export function computeHomingVelocity(
  x: number,
  y: number,
  vx: number,
  vy: number,
  target: Vec2 | null,
  turnRate: number,
): Vec2 | null {
  if (!target) return null;
  const speed = Math.hypot(vx, vy);
  if (speed <= 0) return null;
  const desired = Math.atan2(target.y - y, target.x - x);
  const current = Math.atan2(vy, vx);
  const next = rotateTowards(current, desired, turnRate);
  return { x: Math.cos(next) * speed, y: Math.sin(next) * speed };
}
